import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ApplicationMetricData,
  ApplicationMetricsResponse,
  applicationsApi,
  MetricSetting
} from "api/applications";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

// FE uses boolean flags (badge, role_label), BE persists them in settings[]
const serializeMetric = (metric: ApplicationMetricData) => {
  const settings: MetricSetting[] = [];
  if (metric.badge) settings.push("badge");
  if (metric.role_label) settings.push("role");
  return {
    ...metric,
    settings
  };
};

export const useConfigurableGroupMetric = (enabled = true) => {
  const { applicationId } = useParams();
  const queryClient = useQueryClient();

  const metricsDataKey = ["APPLICATION", applicationId, "metrics"];

  const { data: metricsData, isPending: isMetricsPending } =
    useQuery<ApplicationMetricsResponse>({
      queryKey: metricsDataKey,
      queryFn: () =>
        applicationsApi.getApplicationMetrics(String(applicationId)),
      enabled: enabled && Boolean(applicationId)
    });

  const { mutate: updateMetricsTemplate } = useMutation({
    mutationKey: ["APPLICATION_METRICS_TEMPLATE", "UPDATE"],
    mutationFn: applicationsApi.updateApplicationMetricsTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: metricsDataKey,
        refetchType: "all"
      });
    },
    onError: (err) => toast.error(getErrorMessage(err.message))
  });

  const { mutate: resetMetricsToDefault, isPending: isResetting } = useMutation(
    {
      mutationKey: ["APPLICATION_METRICS_TEMPLATE", "DELETE"],
      mutationFn: applicationsApi.resetApplicationMetricsTemplate,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["APPLICATION", String(applicationId), "metrics"]
        });
        toast.success("Metrics reset to default");
      },
      onError: (err) => {
        toast.error(getErrorMessage(err.message));
      }
    }
  );

  // Prevent reading stale metrics when attempting to update
  const getCurrentMetrics = () => {
    const currentMetrics =
      queryClient.getQueryData<ApplicationMetricsResponse>(
        metricsDataKey
      )?.metrics;
    if (!currentMetrics)
      toast.error("Something went wrong. Please try again later");
    return currentMetrics;
  };

  const removeMetricByIndex = (index: number) => {
    const currentMetrics = getCurrentMetrics();
    if (!currentMetrics) return;

    const updatedMetrics = currentMetrics.filter(
      (metric) => metric.index !== index
    );
    updateMetricsTemplate(updatedMetrics, {
      onSuccess: () => toast.success("Metric removed successfully")
    });
  };

  const addNewMetric = (
    metricData: ApplicationMetricData,
    onSuccess: () => void
  ) => {
    const currentMetrics = getCurrentMetrics();
    if (!currentMetrics) return;

    const hasExistingMetrics = Boolean(currentMetrics.length);
    const newMetricIdx = hasExistingMetrics
      ? (currentMetrics.at(-1)?.index || 0) + 1
      : 1;
    const newMetric = serializeMetric({
      ...metricData,
      index: newMetricIdx
    });
    const updatedMetrics = hasExistingMetrics
      ? [...currentMetrics, newMetric]
      : [newMetric];

    updateMetricsTemplate(updatedMetrics, {
      onSuccess: () => {
        toast.success("Metric added successfully");
        onSuccess();
      }
    });
  };

  const updateMetric = async (
    newMetric: ApplicationMetricData,
    onSuccess: () => void
  ) => {
    const currentMetrics = getCurrentMetrics();
    if (!currentMetrics) return;

    const updatedMetrics = currentMetrics.map((currentMetric) =>
      currentMetric.index === newMetric.index
        ? serializeMetric(newMetric)
        : currentMetric
    );
    updateMetricsTemplate(updatedMetrics, {
      onSuccess: () => {
        toast.success("Metric updated successfully");
        onSuccess();
      }
    });
  };

  return {
    metricsData,
    isMetricsPending,
    resetMetricsToDefault,
    isResetting,
    removeMetricByIndex,
    addNewMetric,
    updateMetric
  };
};
